/**
 * moneyUi.js — the shared vocabulary for every money dashboard.
 * ─────────────────────────────────────────────────────────────────────────────
 * Three audiences read the same order economics and must never see it drawn
 * three different ways: one currency formatter, one KPI tile, one window picker,
 * one chart style, one live-refresh hook. A new money panel composes these; it
 * does not restate them.
 *
 * Nothing here fetches or decides what a reader is allowed to see. The server
 * owns that: /api/money/platform, /api/money/partner/:id, /api/money/vendor/:id
 * each return only that audience's figures, so a component cannot leak a number
 * it was never sent.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { getTokenFromLocalStorage } from "../../utilities/tokenUtils.js";
import { getActiveCountry } from "../../utilities/activeCountry.js";

export const C = {
  navy: "#0b1240",
  teal: "#136274",
  gold: "#9a6e10",
  green: "#0f9d58",
  red: "#d64545",
  pink: "#f5327a",
  ink: "#0f172a",
  body: "#334155",
  muted: "#6b7a99",
  faint: "#8a97ad",
  line: "#eef1f6",
  lineSoft: "#f4f6fb",
  wash: "#f8fafc",
};

/* ── formatting ─────────────────────────────────────────────────────────────
   Money is always shown whole: a shilling decimal on a dashboard is noise, and
   these figures are already rounded server-side. */
export const fmtUGX = (n) => "UGX " + Math.round(Number(n) || 0).toLocaleString();
export const fmtShort = (n) => {
  const v = Math.round(Number(n) || 0);
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1_000_000) return sign + (a / 1_000_000).toFixed(a % 1_000_000 ? 1 : 0) + "M";
  if (a >= 1_000) return sign + (a / 1_000).toFixed(a % 1_000 ? 1 : 0) + "k";
  return sign + String(a);
};
export const fmtPct = (n, dp = 1) => `${(Number(n) || 0).toFixed(dp)}%`;
export const fmtMinutes = (m) => {
  const v = Number(m);
  if (!Number.isFinite(v) || v <= 0) return "n/a";
  return v < 60 ? `${Math.round(v)}m` : `${(v / 60).toFixed(1)}h`;
};
/** "Sep 14" from an ISO day key, for a chart axis that has to stay narrow. */
export const dayLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
};

export const VEHICLE_LABEL = {
  MOTORCYCLE: "Motorcycle",
  CAR: "Car",
  VAN: "Van",
  TRUCK: "Truck",
};
export const RAIL_LABEL = {
  MOMO: "MTN mobile money",
  AIRTEL: "Airtel money",
  FLW: "Flutterwave",
  CARD: "Card",
  COD: "Cash on delivery",
  OTHER: "Other",
};
export const ZONE_LABEL = {
  urban: "Urban",
  peri_urban: "Peri-urban",
  upcountry: "Upcountry",
  unknown: "Unknown",
};

export const S = {
  wrap: {
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 6px 22px rgba(15,23,42,0.07)",
    padding: "22px 24px",
    margin: "0 0 24px",
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 18,
  },
  title: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontWeight: 700,
    fontSize: "1.15rem",
    color: C.ink,
    margin: 0,
  },
  subtitle: { fontSize: 12.5, color: C.faint, marginTop: 4 },
  toggle: { display: "inline-flex", background: "#f1f4f8", borderRadius: 10, padding: 3, gap: 2 },
  tBtn: (on) => ({
    border: "none",
    background: on ? "#fff" : "transparent",
    color: on ? C.navy : "#64748b",
    fontWeight: 600,
    fontSize: 13,
    padding: "7px 14px",
    borderRadius: 8,
    cursor: "pointer",
    boxShadow: on ? "0 1px 4px rgba(15,23,42,.12)" : "none",
  }),
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
    marginBottom: 20,
  },
  card: (accent) => ({
    background: C.wash,
    border: `1px solid ${C.line}`,
    borderLeft: `4px solid ${accent}`,
    borderRadius: 12,
    padding: "14px 16px",
  }),
  cardLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".05em",
    color: "#90a0b7",
    marginBottom: 6,
  },
  cardValue: { fontSize: "1.5rem", fontWeight: 800, color: C.ink, lineHeight: 1.1 },
  cardMeta: { fontSize: 12.5, color: C.muted, marginTop: 6 },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: ".05em",
    color: "#90a0b7",
    margin: "16px 0 10px",
  },
  chartBox: { height: 240, marginBottom: 8 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13.5 },
  th: {
    textAlign: "left",
    padding: "8px 10px",
    color: "#64748b",
    fontSize: 11.5,
    textTransform: "uppercase",
    letterSpacing: ".03em",
    borderBottom: `2px solid ${C.line}`,
    whiteSpace: "nowrap",
  },
  td: { padding: "9px 10px", borderBottom: `1px solid ${C.lineSoft}`, whiteSpace: "nowrap" },
  tdNum: {
    padding: "9px 10px",
    borderBottom: `1px solid ${C.lineSoft}`,
    whiteSpace: "nowrap",
    textAlign: "right",
  },
  scroll: { maxHeight: 360, overflow: "auto", border: `1px solid ${C.line}`, borderRadius: 12 },
  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
    gap: 16,
    marginTop: 18,
  },
  panel: { border: `1px solid ${C.line}`, borderRadius: 12, padding: "14px 16px" },
  row: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "7px 0",
    borderBottom: `1px solid ${C.lineSoft}`,
    fontSize: 13.5,
  },
  muted: { color: C.faint, fontSize: 13 },
  pill: {
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 6,
    background: "#eef2fb",
    color: C.navy,
  },
  note: {
    fontSize: 12.5,
    color: C.muted,
    background: C.wash,
    border: `1px solid ${C.line}`,
    borderRadius: 10,
    padding: "10px 12px",
    marginTop: 14,
  },
};

/** The windows a reader actually asks for. `days` is what the API takes. */
export const WINDOWS = [
  { id: 7, label: "7 days" },
  { id: 30, label: "30 days" },
  { id: 90, label: "90 days" },
];

export function WindowPicker({ value, onChange, options = WINDOWS }) {
  return (
    <div style={S.toggle} role="tablist" aria-label="Reporting window">
      {options.map((w) => (
        <button
          key={w.id}
          type="button"
          style={S.tBtn(value === w.id)}
          onClick={() => onChange(w.id)}
          aria-pressed={value === w.id}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}

export function Kpi({ label, value, meta, accent = C.teal }) {
  return (
    <div style={S.card(accent)}>
      <div style={S.cardLabel}>{label}</div>
      <div style={S.cardValue}>{value}</div>
      {meta ? <div style={S.cardMeta}>{meta}</div> : null}
    </div>
  );
}

export function KpiRow({ children }) {
  return <div style={S.cards}>{children}</div>;
}

export function SectionTitle({ children, first = false }) {
  return <div style={first ? { ...S.sectionTitle, marginTop: 0 } : S.sectionTitle}>{children}</div>;
}

/** A label / value line. Used for every breakdown panel so they all read alike. */
export function MoneyRow({ label, value, sub }) {
  return (
    <div style={S.row}>
      <span style={{ fontWeight: 600, color: C.body }}>{label}</span>
      <span style={{ color: C.muted, textAlign: "right" }}>
        {value}
        {sub ? <span style={{ color: C.faint }}> · {sub}</span> : null}
      </span>
    </div>
  );
}

/**
 * A panel of proportions: the share bar every breakdown uses, so "MTN is most of
 * our volume" is legible without reading the numbers.
 */
export function ShareList({ rows, emptyText = "Nothing in this window yet." }) {
  const total = rows.reduce((s, r) => s + Math.abs(Number(r.amount) || 0), 0) || 1;
  if (!rows.length) return <div style={S.muted}>{emptyText}</div>;
  return (
    <div>
      {rows.map((r) => {
        const share = Math.round((Math.abs(Number(r.amount) || 0) / total) * 100);
        return (
          <div key={r.key} style={{ padding: "8px 0", borderBottom: `1px solid ${C.lineSoft}` }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                fontSize: 13.5,
                marginBottom: 5,
              }}
            >
              <span style={{ fontWeight: 600, color: C.body }}>{r.label}</span>
              <span style={{ color: C.muted }}>
                {fmtUGX(r.amount)}
                {r.sub ? <span style={{ color: C.faint }}> · {r.sub}</span> : null}
              </span>
            </div>
            <div style={{ height: 6, background: "#eef2f7", borderRadius: 4, overflow: "hidden" }}>
              <div
                style={{
                  width: `${share}%`,
                  height: "100%",
                  background: r.color || C.teal,
                  borderRadius: 4,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Shape a signed ladder into the two stacked datasets a waterfall needs: an
 * invisible `bases` series that lifts each step to where the running total sits,
 * and a visible `spans` series that is the step itself.
 *
 * The first and last entries are TOTALS and sit on the axis; everything between
 * them is a step and arrives already signed, so a deduction is negative and a
 * recovery (a processing fee collected back from the customer) is positive. A
 * closing total stays signed so a loss draws below the axis instead of being
 * flattered into a positive bar.
 *
 * @param {Array<{label:string, amount:number, kind:"in"|"out"|"net"}>} ladder
 */
export function waterfallSeries(ladder = []) {
  const bases = [];
  const spans = [];
  let running = 0;
  const last = ladder.length - 1;
  ladder.forEach((step, i) => {
    const amount = Number(step?.amount) || 0;
    if (i === 0) {
      bases.push(0);
      spans.push(amount);
      running = amount;
    } else if (i === last) {
      bases.push(0);
      spans.push(amount);
    } else {
      /* A step spans from where the running total was to where it now is. Taking
         the LOWER of the two is what lets an inflow (a processing fee recovered)
         sit in the middle of a ladder of deductions without drawing upside down. */
      const before = running;
      running += amount;
      bases.push(Math.min(before, running));
      spans.push(Math.abs(amount));
    }
  });
  return { bases, spans };
}

/** Shared Chart.js options for a money bar chart. `series` labels the tooltip. */
export function moneyBarOptions({ stacked = false, tooltipLabel } = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 }, color: C.muted },
      },
      tooltip: {
        callbacks: {
          label: (ctx) =>
            tooltipLabel
              ? tooltipLabel(ctx)
              : `${ctx.dataset.label}: ${fmtUGX(ctx.parsed.y ?? ctx.parsed.x)}`,
        },
      },
    },
    scales: {
      y: {
        stacked,
        beginAtZero: true,
        ticks: { callback: (v) => fmtShort(v), color: C.faint, font: { size: 11 } },
        grid: { color: "#f1f4f8" },
      },
      x: {
        stacked,
        ticks: { color: C.faint, font: { size: 11 }, maxRotation: 0, autoSkip: true },
        grid: { display: false },
      },
    },
  };
}

export function Loading({ what = "figures" }) {
  return (
    <div style={{ padding: "40px 0", textAlign: "center", color: C.faint }}>Loading {what}...</div>
  );
}

export function ErrorNote({ children }) {
  return <div className="alert alert-warning mb-0">{children}</div>;
}

/**
 * Fetch + keep fresh. Every money panel wants the same thing: load on mount,
 * reload when the window changes, and quietly re-fetch when an order moves. The
 * socket refresh is debounced so a burst of orders costs one request, and a
 * silent failure never replaces a good view with an error.
 *
 * @param {Function} fetcher async (days) => axios response
 * @param {Array}    deps    extra dependencies that invalidate the report
 */
export function useMoneyReport(fetcher, deps = [], { days = 30, events } = {}) {
  const [window_, setWindow] = useState(days);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const daysRef = useRef(window_);
  daysRef.current = window_;
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;
  // Same treatment as `fetcher`: callers pass an inline array, so listing
  // `events` as a dependency would tear down and rebuild the socket on every
  // render. Through a ref the effect reads the latest value and still runs once.
  const eventsRef = useRef(events);
  eventsRef.current = events;

  // `deps` is caller-supplied and variable-length, so it cannot be spread into
  // a dependency array: React requires a statically sized one, and spreading is
  // exactly what the exhaustive-deps directive here used to paper over. Every
  // call site passes primitives ([], [vendorId], [partnerId, asStaff]), so one
  // serialised key carries the same information in a fixed slot.
  const depsKey = JSON.stringify(deps);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await fetchRef.current(daysRef.current);
      setData(res?.data || null);
      setError(null);
      setRefreshedAt(new Date());
    } catch (e) {
      const msg =
        e?.response?.data?.message || e?.response?.data?.error || "Couldn't load these figures.";
      if (!silent) setError(msg);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, window_, depsKey]);

  useEffect(() => {
    const url = process.env.REACT_APP_SERVER_URL;
    if (!url) return undefined;
    const token = getTokenFromLocalStorage && getTokenFromLocalStorage();
    const socket = io(url, token ? { auth: { token } } : undefined);
    const join = () => {
      try {
        socket.emit("join", `dashboardRoom:${getActiveCountry()}`);
      } catch (_e) {
        /* the room is a nicety; polling on reconnect still works */
      }
    };
    socket.on("connect", join);
    let timer = null;
    const bump = () => {
      clearTimeout(timer);
      timer = setTimeout(() => load({ silent: true }), 2500);
    };
    (eventsRef.current || ["newOrderAlert", "dashboardUpdate", "orderDelivered"]).forEach(
      (ev) => socket.on(ev, bump)
    );
    return () => {
      clearTimeout(timer);
      socket.off();
      socket.disconnect();
    };
  }, [load]);

  return { data, loading, error, days: window_, setDays: setWindow, reload: load, refreshedAt };
}

/**
 * The one-line honesty footer. A dashboard that cannot say which of its numbers
 * is a provider figure and which is an estimate invites a reader to bet on a
 * guess, so every panel that shows a rail cost prints this.
 */
export function railConfidenceNote(totals) {
  const pct = Number(totals?.railConfidencePct);
  if (!Number.isFinite(pct)) return null;
  if (pct >= 99.5) {
    return "Payment processing charges are the figures the providers reported.";
  }
  return (
    `${fmtPct(pct, 0)} of payment processing charges are provider-reported figures; ` +
    "the rest is estimated at the configured rate for orders placed before charge capture began."
  );
}

export default S;
